/**
 * Authentication Utilities
 * Handles secure role-based authentication flow
 */

import { db } from '../firebaseconf';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

/**
 * CRITICAL: Fetch user's role from the authoritative source
 * This must be called after Firebase Auth succeeds
 * 
 * The users collection is the SOURCE OF TRUTH for roles
 * 
 * @param {string} uid - Firebase Authentication UID
 * @returns {Promise<object>} User object with role, institutionId, profileId, etc.
 *         Returns null if user document doesn't exist
 * 
 * @throws {Error} If database fetch fails
 */
export const fetchUserRoleDocumentAfterLogin = async (uid) => {
  if (!uid) {
    throw new Error('Firebase UID is required to fetch user role');
  }

  try {
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      // User document doesn't exist - this is an error state
      // User shouldn't exist in Firebase Auth but not in Firestore
      console.warn(`⚠️ User ${uid} authenticated but no role document found in Firestore`);
      return null;
    }

    const userData = userSnap.data();
    
    // Validate required fields
    if (!userData.role) {
      throw new Error('Role field missing in user document');
    }

    console.log(`✅ Fetched user role: ${userData.role} for ${userData.email}`);

    return {
      uid: uid,
      email: userData.email,
      role: userData.role,
      institutionId: userData.institutionId,
      profileId: userData.profileId,
      name: userData.name,
      status: userData.status,
      createdAt: userData.createdAt
    };
  } catch (error) {
    console.error('Error fetching user role document:', error);
    throw error;
  }
};

/**
 * Create user role document during registration
 * This is called when:
 * - Admin registers new institution
 * - Admin adds a teacher
 * - Admin adds a student with login
 * 
 * @param {string} uid - Firebase Authentication UID
 * @param {string} email - User's email
 * @param {string} role - User role ("admin", "teacher", "student", "superadmin")
 * @param {string} institutionId - Institution ID (null for superadmin)
 * @param {string} profileId - ID in institution's subcollection (admin_001, teacher_001, etc.)
 * @param {object} additionalData - Any extra fields (name, status, etc.)
 * @returns {Promise<void>}
 * 
 * @throws {Error} If database operation fails
 */
export const createUserRoleDocument = async (uid, email, role, institutionId, profileId, additionalData = {}) => {
  if (!uid || !email || !role) {
    throw new Error('uid, email, and role are required to create user document');
  }

  // Validate role
  const validRoles = ['superadmin', 'admin', 'teacher', 'student'];
  if (!validRoles.includes(role.toLowerCase())) {
    throw new Error(`Invalid role: ${role}. Must be one of: ${validRoles.join(', ')}`);
  }

  // Super admin doesn't need institutionId
  if (role !== 'superadmin' && !institutionId) {
    throw new Error(`Institution ID required for role: ${role}`);
  }

  try {
    const userRef = doc(db, 'users', uid);
    
    const userData = {
      email: email.toLowerCase().trim(),
      role: role.toLowerCase(),
      institutionId: institutionId || null,
      profileId: profileId || null,
      name: additionalData.name || email.split('@')[0],
      status: additionalData.status || 'active',
      createdAt: serverTimestamp(),
      ...additionalData
    };

    await setDoc(userRef, userData);

    console.log(`✅ Created user role document for ${email} with role ${role}`);
    return userData;
  } catch (error) {
    console.error('Error creating user role document:', error);
    throw error;
  }
};

/**
 * Determine redirect URL based on user role
 * 
 * @param {string} role - User role
 * @returns {string} Redirect URL path
 */
export const getRedirectUrlForRole = (role) => {
  if (!role) {
    return '/login';
  }

  const roleLower = role.toLowerCase();

  switch (roleLower) {
    case 'superadmin':
      return '/super-admin-dashboard';
    case 'admin':
    case 'principal':
      return '/admin-dashboard';
    case 'teacher':
      return '/teacher-dashboard';
    case 'student':
      return '/dashboard';
    default:
      console.warn(`Unknown role: ${role}, redirecting to home`);
      return '/';
  }
};

/**
 * Complete authentication flow after Firebase Auth succeeds
 * 
 * CRITICAL FLOW:
 * 1. User enters email/password → Firebase Auth succeeds
 * 2. Get Firebase UID from userCredential
 * 3. Call this function with the UID
 * 4. This fetches role from users/{uid} (AUTHORITATIVE)
 * 5. Store complete user data in sessionStorage
 * 6. Return redirect URL
 * 
 * @param {string} uid - Firebase Auth UID
 * @returns {Promise<object>} { userRole, institution Id, redirectUrl, userData }
 * 
 * @throws {Error} If user role document doesn't exist or other issues
 */
export const completeAuthenticationFlow = async (uid) => {
  if (!uid) {
    throw new Error('Firebase UID is required');
  }

  try {
    // Step 1: Fetch authoritative role from users collection
    const userRole = await fetchUserRoleDocumentAfterLogin(uid);

    if (!userRole) {
      throw new Error(
        'User authenticated but role document not found. ' +
        'User must complete registration or contact administrator.'
      );
    }

    // Step 2: Determine redirect URL
    const redirectUrl = getRedirectUrlForRole(userRole.role);

    // Step 3: Prepare data for storage
    const authData = {
      uid: uid,
      email: userRole.email,
      role: userRole.role,
      institutionId: userRole.institutionId,
      profileId: userRole.profileId,
      name: userRole.name,
      status: userRole.status,
      loginTime: new Date().toISOString(),
      // Legacy field names for compatibility
      institution_id: userRole.institutionId,
      id: uid
    };

    console.log('✅ Authentication flow completed successfully');

    return {
      success: true,
      userRole: userRole.role,
      institutionId: userRole.institutionId,
      redirectUrl: redirectUrl,
      userData: authData
    };
  } catch (error) {
    console.error('Authentication flow failed:', error);
    return {
      success: false,
      error: error.message,
      redirectUrl: '/login'
    };
  }
};

/**
 * Validate that user has access to institution
 * Used in ProtectedRoute to prevent users from accessing institutions they don't belong to
 * 
 * @param {object} user - User object from sessionStorage or auth context
 * @param {string} requiredInstitutionId - Institution to check access for
 * @returns {boolean} True if user has access
 */
export const userHasInstitutionAccess = (user, requiredInstitutionId) => {
  if (!user) {
    return false;
  }

  // Super admin has access to all institutions
  if (user.role === 'superadmin' || user.role === 'super_admin') {
    return true;
  }

  // For other roles, check institutionId matches
  const userInstitutionId = user.institution_id || user.institutionId || user.institutionInfo;
  return userInstitutionId === requiredInstitutionId;
};

/**
 * Normalize role name (handle variation between "superadmin" and "super_admin")
 * 
 * @param {string} role - Role to normalize
 * @returns {string} Normalized role
 */
export const normalizeRole = (role) => {
  if (!role) return null;
  
  const normalized = role.toLowerCase().replace(/_/g, '');
  
  const roleMap = {
    'superadmin': 'superadmin',
    'admin': 'admin',
    'principal': 'admin', // Treat principal as admin
    'teacher': 'teacher',
    'student': 'student'
  };

  return roleMap[normalized] || role.toLowerCase();
};
